import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Container, Heading, IconButton, Muted, Pressable, Screen, Stack, Text } from '@fluent/ui';
import { EXAM_MAX_SCORE } from '@fluent/types';
import { useAuth } from '../auth/authStore.js';
import { useProAccessState } from '../subscription/hooks.js';
import { EVERYTHING_FREE } from '../subscription/freeMode.js';
import { AppBottomNav } from '../nav/AppBottomNav.js';
import { EXAM_LEVELS, isFreeExamTest, isTestAvailable, numberOf, testIdOf } from '../exam/catalog.js';
import { formatClock, remainingMs } from '../exam/attempt.js';
import { useExamStore, useUserExams } from '../exam/examStore.js';
import { ExamInfoDialog, ExamNotice } from '../exam/ExamInfo.js';
import { useExamResultsSync } from '../exam/hooks.js';
import '../exam/Exam.css';

/**
 * ტესტი tab — the national-exam simulator's front door (replaces the old
 * Practice tab; Tornike 2026-09-25). 120 tests in five levels, easiest
 * first. Each tile shows its state at a glance: not started, in progress
 * (time left), finished (best score), locked (no Pro), or not written yet.
 *
 * `demo` renders the same screen without sign-in or a subscription, for
 * the review route Tornike checks designs on.
 */

export interface ExamHomeProps {
  demo?: boolean;
  basePath?: string;
}

export function ExamHome({ demo = false, basePath = '/test' }: ExamHomeProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const realAccess = useProAccessState();
  const access = demo || EVERYTHING_FREE ? 'yes' : realAccess;
  const userKey = demo ? 'demo' : (user?.id ?? '');
  const exams = useUserExams(userKey);
  const markIntroSeen = useExamStore((s) => s.markIntroSeen);
  // Finished tests follow the account: a new device shows the same scores.
  useExamResultsSync(!demo);
  const [infoOpen, setInfoOpen] = useState(false);

  // First visit: explain the two differences from the real exam once.
  useEffect(() => {
    if (userKey && !exams.introSeen) {
      setInfoOpen(true);
      markIntroSeen(userKey);
    }
  }, [userKey, exams.introSeen, markIntroSeen]);

  if (!demo && !user) return null;

  const inProgress = Object.values(exams.active).sort((a, b) => b.startedAt - a.startedAt)[0];
  const open = (testId: string) => {
    if (access === 'no' && !isFreeExamTest(numberOf(testId))) {
      navigate('/subscription');
      return;
    }
    navigate(`${basePath}/${testId}`);
  };

  return (
    <Screen>
      <Container size="sm">
        <Stack
          gap={6}
          style={{ padding: 'var(--space-6, 24px) 0 calc(var(--space-16, 64px) + 88px + env(safe-area-inset-bottom))' }}
        >
          <div className="fluent-exam__home-head">
            <Stack gap={1}>
              <Heading level={1}>{t('exam.title')}</Heading>
              <Muted>{t('exam.subtitle')}</Muted>
            </Stack>
            <IconButton label={t('exam.info_aria')} onPress={() => setInfoOpen(true)}>
              <span className="fluent-exam__info-glyph" aria-hidden="true">
                i
              </span>
            </IconButton>
          </div>

          <ExamNotice onMore={() => setInfoOpen(true)} />

          {inProgress && (
            <div className="fluent-exam__continue">
              <Stack gap={1}>
                <Text weight="black">
                  {t('exam.continue_title', { n: Number(inProgress.testId.slice(5)) })}
                </Text>
                <Text size="sm" tone="muted">
                  {t('exam.continue_body', { time: formatClock(remainingMs(inProgress.elapsedMs)) })}
                </Text>
              </Stack>
              <Button variant="primary" size="md" onClick={() => open(inProgress.testId)}>
                {t('exam.continue_cta')}
              </Button>
            </div>
          )}

          {EXAM_LEVELS.map(({ level, from, to }) => (
            <section key={level} className="fluent-exam__level" data-level={level}>
              <header className="fluent-exam__level-head">
                <Heading level={3}>{t(`exam.level_${level}`)}</Heading>
                <Text size="sm" tone="muted">
                  {t('exam.level_range', { from, to })}
                </Text>
              </header>
              <div className="fluent-exam__grid">
                {Array.from({ length: to - from + 1 }, (_, k) => {
                  const n = from + k;
                  const id = testIdOf(n);
                  const available = isTestAvailable(id);
                  const active = exams.active[id];
                  const result = exams.results[id];
                  const state = !available
                    ? 'soon'
                    : access === 'no' && !isFreeExamTest(n)
                      ? 'locked'
                      : active
                        ? 'active'
                        : result
                          ? 'done'
                          : 'new';
                  return (
                    <Pressable
                      key={id}
                      className="fluent-exam__tile"
                      data-state={state}
                      disabled={state === 'soon'}
                      onClick={() => open(id)}
                      aria-label={t('exam.tile_aria', { n })}
                    >
                      <span className="fluent-exam__tile-n">{n}</span>
                      <span className="fluent-exam__tile-sub">
                        {state === 'soon'
                          ? t('exam.tile_soon')
                          : state === 'locked'
                            ? '🔒'
                            : state === 'active' && active
                              ? formatClock(remainingMs(active.elapsedMs)).slice(0, -3)
                              : state === 'done' && result
                                ? t('exam.points', { score: result.best.score, max: EXAM_MAX_SCORE })
                                : access === 'no' && isFreeExamTest(n)
                                  ? t('exam.tile_free')
                                  : ''}
                      </span>
                    </Pressable>
                  );
                })}
              </div>
            </section>
          ))}
        </Stack>
      </Container>
      <ExamInfoDialog open={infoOpen} onOpenChange={setInfoOpen} />
      <AppBottomNav />
    </Screen>
  );
}
